export type VoiceType = 'sine' | 'triangle' | 'sawtooth' | 'square' | 'organ' | 'strings' | 'brass' | 'bell' | 'choir';

export const VOICE_LABELS: Record<VoiceType, string> = {
  sine: 'Sine', triangle: 'Triangle', sawtooth: 'Sawtooth', square: 'Square',
  organ: 'Organ', strings: 'Strings', brass: 'Brass', bell: 'Bell', choir: 'Choir',
};

export function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

const ORGAN_HARMONICS: { mul: number; gain: number }[] = [
  { mul: 1, gain: 0.45 }, { mul: 2, gain: 0.22 }, { mul: 3, gain: 0.13 },
  { mul: 4, gain: 0.08 }, { mul: 6, gain: 0.04 },
];

export interface VoiceNodes { oscs: OscillatorNode[]; gain: GainNode; }

// Builds the oscillator graph + attack envelope for a voice into a fresh gain
// node connected to `dest`. The caller owns the release (see releaseVoice),
// except 'bell' which schedules its own full decay and oscillator stop here.
export function buildVoice(
  ctx: AudioContext, midi: number, voice: VoiceType, dest: AudioNode, now: number,
): VoiceNodes {
  const hz = midiToFreq(midi);
  const gain = ctx.createGain();
  gain.connect(dest);
  const oscs: OscillatorNode[] = [];

  const addOsc = (type: OscillatorType, freq: number, target: AudioNode) => {
    const osc = ctx.createOscillator();
    osc.type = type; osc.frequency.value = freq;
    osc.connect(target); osc.start(now);
    oscs.push(osc);
  };

  if (voice === 'organ') {
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.28, now + 0.015);
    for (const { mul, gain: hGain } of ORGAN_HARMONICS) {
      const hg = ctx.createGain(); hg.gain.value = hGain; hg.connect(gain);
      addOsc('sine', hz * mul, hg);
    }
  } else if (voice === 'strings') {
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.2, now + 0.09);
    for (const cents of [-6, 0, 6]) {
      const filt = ctx.createBiquadFilter();
      filt.type = 'lowpass'; filt.frequency.value = 2800; filt.Q.value = 0.6;
      filt.connect(gain);
      addOsc('sawtooth', hz * Math.pow(2, cents / 1200), filt);
    }
  } else if (voice === 'brass') {
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.25, now + 0.022);
    const filt = ctx.createBiquadFilter();
    filt.type = 'bandpass'; filt.frequency.value = hz * 3; filt.Q.value = 1.8;
    filt.connect(gain);
    addOsc('sawtooth', hz, filt);
  } else if (voice === 'bell') {
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.38, now + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 3.0);
    const ratios = [1, 2.756, 5.404, 8.933, 13.354];
    const amps = [0.5, 0.25, 0.12, 0.06, 0.03];
    ratios.forEach((r, i) => {
      const hg = ctx.createGain(); hg.gain.value = amps[i]; hg.connect(gain);
      const osc = ctx.createOscillator();
      osc.type = 'sine'; osc.frequency.value = hz * r;
      osc.connect(hg); osc.start(now); osc.stop(now + 3.1);
      oscs.push(osc);
    });
  } else if (voice === 'choir') {
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.26, now + 0.04);
    ([[-12, 0.12], [0, 0.16], [12, 0.12]] as [number, number][]).forEach(([cents, amp]) => {
      const hg = ctx.createGain(); hg.gain.value = amp; hg.connect(gain);
      addOsc('sine', hz * Math.pow(2, cents / 1200), hg);
    });
  } else {
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.28, now + 0.015);
    addOsc(voice as OscillatorType, hz, gain);
  }

  return { oscs, gain };
}

// Release a sustained voice with a short fade. No-op envelope for 'bell',
// which already scheduled its own decay in buildVoice.
export function releaseVoice(ctx: AudioContext, nodes: VoiceNodes, voice: VoiceType) {
  if (voice === 'bell') return;
  const now = ctx.currentTime;
  nodes.gain.gain.setValueAtTime(nodes.gain.gain.value, now);
  nodes.gain.gain.linearRampToValueAtTime(0, now + 0.08);
  nodes.oscs.forEach(osc => osc.stop(now + 0.09));
}

// Fire-and-forget reference tone for the given duration. Creates and tears down
// its own context so callers don't have to manage one.
export function playTone(midi: number, voice: VoiceType, durationMs: number): void {
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  const now = ctx.currentTime;
  const nodes = buildVoice(ctx, midi, voice, ctx.destination, now);
  const stopAt = now + durationMs / 1000;
  if (voice !== 'bell') {
    nodes.gain.gain.setValueAtTime(nodes.gain.gain.value, Math.max(now, stopAt - 0.08));
    nodes.gain.gain.linearRampToValueAtTime(0, stopAt);
    nodes.oscs.forEach(osc => osc.stop(stopAt + 0.02));
  }
  const closeAt = (voice === 'bell' ? 3.2 : durationMs / 1000 + 0.1) * 1000;
  setTimeout(() => ctx.close().catch(() => {}), closeAt);
}
